<template>
  <div>
    <v-row dense>
      <v-col cols="12">
        <div class="d-flex align-center text-orange-accent-4 mt-3">
          <v-icon icon="mdi-alert" color="orange-accent-4" class="mr-2" />
          <span> Notice: Be sure to save after updating values for it to be reflected. </span>
        </div>
      </v-col>
    </v-row>

    <!-- Custom Attribute Columns -->
    <v-row dense>
      <v-col cols="12">
        <v-combobox
          v-model="customAttributeColumns"
          label="Custom Attribute Columns"
          placeholder="Type and press Enter to add a custom attribute (e.g. iqme:roles)"
          hint="Press Enter after each value. Format: productCode:key"
          variant="underlined"
          color="primary"
          multiple
          chips
          closable-chips
          :error-messages="customAttributeColumnErrors"
          @update:model-value="onCustomAttributeColumnsChange"
        />
      </v-col>
    </v-row>

    <!-- Inline Table View -->
    <v-row dense>
      <v-col cols="8">
        <v-text-field
          v-model="userSearch"
          label="Search"
          append-icon="mdi-magnify"
          variant="underlined"
          color="primary"
        />
      </v-col>
      <v-col cols="4" align="end">
        <cs-button
          primary
          label="Add User"
          class="mt-4"
          left-icon="mdi-plus-circle"
          :disabled="isFormReadOnly"
          @click="onAddClick"
        />
      </v-col>
    </v-row>
    <v-data-table
      :items="inputFields"
      density="compact"
      color="primary"
      height="400"
      fixed-header
      :search="userSearch"
      :headers="tableHeaders"
      :loading="fetchHubCsvUsersInProgress"
    >
      <template #[`item.username`]="{ item }">
        {{ item.username || '-' }}
      </template>
      <template #[`item.emailAddress`]="{ item }">
        {{ item.emailAddress || '-' }}
      </template>
      <template #[`item.displayName`]="{ item }">
        {{ item.displayName || '-' }}
      </template>
      <template #[`item.status`]="{ item }">
        {{ item.status || '-' }}
      </template>
      <template #[`item.actions`]="{ item }">
        <v-icon
          icon="mdi-pencil"
          color="primary"
          :disabled="isFormReadOnly"
          class="mr-2"
          aria-label="Edit Row"
          @click="onEditRowClick(item)"
        />
        <v-icon
          icon="mdi-delete"
          color="primary"
          aria-label="Delete Row"
          :disabled="isFormReadOnly"
          @click="onDeleteClick(item)"
        />
      </template>
    </v-data-table>
    <v-row dense class="mt-4">
      <v-col cols="12" class="d-flex justify-end">
        <cs-button
          primary
          label="Save"
          :loading="saveInProgress"
          :disabled="isFormReadOnly"
          @click="onSaveClick()"
        />
      </v-col>
    </v-row>

    <!-- Form Dialog (Add/Edit User) -->
    <cs-form-dialog
      id="dlg-client-hub-user-data-management-form"
      v-model="formDialogDisplayed"
      body-id="dlg-client-hub-user-data-management-form-body"
      :heading="editingIndex !== null ? 'Edit User' : 'Add User'"
      :primary-action="{
        label: editingIndex !== null ? 'Update' : 'Add',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click'
      }"
      @primary-click="onFormSubmit()"
      @secondary1-click="onFormCancel()"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formFields.tenantSubDomain"
                label="Tenant SubDomain"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.tenantSubDomain"
                @input="formErrors.tenantSubDomain = ''"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="formFields.username"
                label="Username"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.username"
                @input="formErrors.username = ''"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formFields.emailAddress"
                label="Email Address"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.emailAddress"
                @input="formErrors.emailAddress = ''"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="formFields.displayName"
                label="Display Name"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.displayName"
                @input="formErrors.displayName = ''"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formFields.givenName"
                label="Given Name"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.givenName"
                @input="formErrors.givenName = ''"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="formFields.familyName"
                label="Family Name"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.familyName"
                @input="formErrors.familyName = ''"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formFields.preferredLocale"
                label="Preferred Locale"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.preferredLocale"
                @input="formErrors.preferredLocale = ''"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="formFields.status"
                label="Status"
                variant="underlined"
                color="primary"
                :items="statusOptions"
                :error-messages="formErrors.status"
                @input="formErrors.status = ''"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formFields.title"
                label="Title"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.title"
                @input="formErrors.title = ''"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="formFields.phoneNumber"
                label="Phone Number"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.phoneNumber"
                @input="formErrors.phoneNumber = ''"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-select
                v-model="formFields.authenticationType"
                label="Authentication Type"
                variant="underlined"
                color="primary"
                :items="authTypeOptions"
                :error-messages="formErrors.authenticationType"
                @input="formErrors.authenticationType = ''"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="formFields.role"
                label="Role"
                variant="underlined"
                color="primary"
                :items="roleOptions"
                :error-messages="formErrors.role"
                @input="formErrors.role = ''"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="formFields.productInstances"
                label="Product Instances"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.productInstances"
                @input="formErrors.productInstances = ''"
              />
            </v-col>
            <v-col cols="6" />
          </v-row>
          <v-row v-for="col in customAttributeColumns" :key="col">
            <v-col cols="12">
              <v-text-field
                v-model="formFields.customAttributes[col]"
                :label="col"
                variant="underlined"
                color="primary"
              />
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <!-- Success Dialog -->
    <cs-form-dialog
      id="dlg-client-hub-user-data-management-save-success"
      v-model="showSaveSuccessDialog"
      heading="Save Success"
      body-id="dlg-client-hub-user-data-management-save-success-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showSaveSuccessDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p>The user information has been saved successfully.</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <!-- Error Dialog -->
    <cs-form-dialog
      id="dlg-client-hub-user-data-management-save-error"
      v-model="showSaveErrorDialog"
      heading="Save Error"
      body-id="dlg-client-hub-user-data-management-save-error-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showSaveErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p>There is some error while saving the user information. Please try again.</p>
                <p class="text-red-accent-4">{{ errorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-user-data-management-validation-error"
      v-model="showValidationErrorDialog"
      heading="Validation Error"
      body-id="dlg-client-hub-user-data-management-validation-error-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showValidationErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p class="text-red-accent-4">{{ validationErrorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-user-data-management-fetch-error"
      v-model="fetchHubCsvUsersErrorDialog"
      heading="Fetch User Error"
      body-id="dlg-client-hub-user-data-management-fetch-error-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="fetchHubCsvUsersErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p>There is some error while fetching the user information. Please try again.</p>
                <p class="text-red-accent-4">{{ fetchHubCsvUsersErrorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { validationRules } from '@/helpers/validationRules';
import * as types from '@/store/mutationTypes';

const FIELD_MAP = {
  tenantSubDomain: 'TenantSubDomain',
  username: 'Username',
  emailAddress: 'EmailAddress',
  familyName: 'FamilyName',
  givenName: 'GivenName',
  displayName: 'DisplayName',
  preferredLocale: 'PreferredLocale',
  status: 'Status',
  title: 'Title',
  phoneNumber: 'PhoneNumber',
  authenticationType: 'AuthenticationType',
  role: 'Role',
  productInstances: 'ProductInstances'
};

const KNOWN_API_KEYS = new Set(Object.values(FIELD_MAP));

const mapUserFields = (source, customCols, isFormatToApi = false) => {
  const entries = Object.entries(FIELD_MAP);
  if (isFormatToApi) {
    const base = Object.fromEntries(entries.map(([ui, api]) => [api, source[ui]]));
    const custom = Object.fromEntries(
      customCols.map((col) => [col, source.customAttributes[col] || ''])
    );
    return { ...base, ...custom };
  }
  const base = Object.fromEntries(entries.map(([ui, api]) => [ui, source[api]]));
  base.authenticationType = base.authenticationType || 'Internal';
  base.role = base.role || 'User';
  const customAttributes = Object.fromEntries(customCols.map((col) => [col, source[col] || '']));
  return { ...base, customAttributes };
};

const DEFAULT_FORM_FIELDS = {
  tenantSubDomain: '',
  username: '',
  emailAddress: '',
  familyName: '',
  givenName: '',
  displayName: '',
  preferredLocale: 'en-gb',
  status: 'Active',
  title: '',
  phoneNumber: '',
  authenticationType: 'Internal',
  role: 'User',
  productInstances: 'default',
  customAttributes: {}
};

const DEFAULT_FORM_ERRORS = {
  tenantSubDomain: '',
  username: '',
  emailAddress: '',
  familyName: '',
  givenName: '',
  displayName: '',
  preferredLocale: '',
  status: '',
  title: '',
  phoneNumber: '',
  authenticationType: '',
  role: '',
  productInstances: ''
};

export default {
  name: 'ClientHubUserDataManagement',

  props: {
    clientSid: { type: String, required: false, default: '' },
    uploadFileInProgress: { type: Boolean, required: false, default: false }
  },

  emits: ['save-success'],

  data() {
    return {
      userSearch: '',
      customAttributeColumns: [],
      customAttributeColumnErrors: '',
      tableHeaders: [
        { title: 'Username', key: 'username' },
        { title: 'Email', key: 'emailAddress' },
        { title: 'Display Name', key: 'displayName' },
        { title: 'Status', key: 'status' },
        { title: 'Actions', key: 'actions', align: 'center' }
      ],
      formDialogDisplayed: false,
      inputFields: [],
      editingIndex: null,
      formFields: cloneDeep(DEFAULT_FORM_FIELDS),
      formErrors: cloneDeep(DEFAULT_FORM_ERRORS),
      statusOptions: ['Active', 'Inactive', 'Delete'],
      authTypeOptions: ['Internal', 'External'],
      roleOptions: ['TenantAdministrator', 'Support', 'User'],
      fetchHubCsvUsersInProgress: false,
      fetchHubCsvUsersErrorDialog: false,
      fetchHubCsvUsersErrorMessage: '',
      saveInProgress: false,
      showSaveSuccessDialog: false,
      showSaveErrorDialog: false,
      errorMessage: '',
      showValidationErrorDialog: false,
      validationErrorMessage: ''
    };
  },

  computed: {
    isFormReadOnly() {
      return this.saveInProgress || this.uploadFileInProgress || this.fetchHubCsvUsersInProgress;
    }
  },

  async mounted() {
    await this.fetchHubCsvUsers();
  },

  methods: {
    onCustomAttributeColumnsChange(newColumns) {
      const invalid = newColumns.filter((col) => !col || !/^[^:]+:[^:]+$/.test(col));

      const validColumns = newColumns.filter((col) => col && /^[^:]+:[^:]+$/.test(col));
      this.customAttributeColumns = validColumns;

      if (invalid.length > 0) {
        this.customAttributeColumnErrors =
          'All headers must be in format "productCode:key" (e.g. iqme:roles)';
      } else {
        this.customAttributeColumnErrors = '';
      }

      const currentKeys = Object.keys(this.inputFields[0]?.customAttributes || {});
      const removedKeys = currentKeys.filter((key) => !newColumns.includes(key));
      this.inputFields.forEach((item) => {
        removedKeys.forEach((key) => delete item.customAttributes[key]);
      });
    },

    onDeleteClick(item) {
      const index = this.inputFields.indexOf(item);
      this.inputFields.splice(index, 1);
    },

    onAddClick() {
      this.editingIndex = null;
      this.formFields = {
        ...cloneDeep(DEFAULT_FORM_FIELDS),
        customAttributes: Object.fromEntries(this.customAttributeColumns.map((col) => [col, '']))
      };
      this.formErrors = cloneDeep(DEFAULT_FORM_ERRORS);
      this.formDialogDisplayed = true;
    },

    onEditRowClick(item) {
      const index = this.inputFields.indexOf(item);
      this.editingIndex = index;
      this.formFields = cloneDeep(this.inputFields[index]);
      this.formErrors = cloneDeep(DEFAULT_FORM_ERRORS);
      this.formDialogDisplayed = true;
    },

    onFormSubmit() {
      const isValid = this.validateForm();
      if (!isValid) return;

      if (this.editingIndex !== null) {
        this.inputFields[this.editingIndex] = cloneDeep(this.formFields);
      } else {
        this.inputFields.push(cloneDeep(this.formFields));
      }
      this.formDialogDisplayed = false;
    },

    onFormCancel() {
      this.formDialogDisplayed = false;
    },

    validateTenantSubDomain(v) {
      const req = validationRules.required('Tenant subdomain')(v);
      if (req !== true) return req;
      const min = validationRules.minLength('Tenant subdomain', 2)(v);
      if (min !== true) return min;
      const max = validationRules.maxLength('Tenant subdomain', 31)(v);
      if (max !== true) return max;
      return validationRules.pattern(
        'Tenant subdomain',
        /^[a-z0-9](?:[a-z0-9-]{0,29}[a-z0-9])?$/,
        'Lowercase letters, numbers, and hyphens only. Must start and end with a letter or number.'
      )(v);
    },

    validateUsername(v) {
      const req = validationRules.required('Username')(v);
      if (req !== true) return req;
      const max = validationRules.maxLength('Username', 128)(v);
      if (max !== true) return max;
      const pattern = validationRules.pattern(
        'Username',
        /^[\p{L}\p{M}\p{S}\p{N}\p{P}]+$/u,
        'No whitespace or control characters allowed.'
      )(v);
      if (pattern !== true) return pattern;
      const duplicate = this.inputFields.some(
        (item, i) => i !== this.editingIndex && item.username.toLowerCase() === v.toLowerCase()
      );
      if (duplicate) return 'Username already exists (case-insensitive)';
      return true;
    },

    validateEmailAddress(v) {
      const req = validationRules.required('Email address')(v);
      if (req !== true) return req;
      const email = validationRules.email(v);
      if (email !== true) return email;
      if (v !== v.toLowerCase()) return 'Email address must be lowercase';
      return true;
    },

    validateFamilyName(v) {
      const req = validationRules.required('Family name')(v);
      if (req !== true) return req;
      return validationRules.maxLength('Family name', 64)(v);
    },

    validateGivenName(v) {
      const req = validationRules.required('Given name')(v);
      if (req !== true) return req;
      return validationRules.maxLength('Given name', 64)(v);
    },

    validateDisplayName(v) {
      const req = validationRules.required('Display name')(v);
      if (req !== true) return req;
      return validationRules.maxLength('Display name', 256)(v);
    },

    validatePreferredLocale(v) {
      const req = validationRules.required('Preferred locale')(v);
      if (req !== true) return req;
      return validationRules.oneOf('Preferred locale', ['en-gb'])(v);
    },

    validateStatus(v) {
      const req = validationRules.required('Status')(v);
      if (req !== true) return req;
      return validationRules.oneOf('Status', ['Active', 'Inactive', 'Delete'])(v);
    },

    validateTitle(v) {
      if (!v) return true;
      return validationRules.maxLength('Title', 250)(v);
    },

    validatePhoneNumber(v) {
      if (!v) return true;
      const pattern = validationRules.pattern(
        'Phone number',
        /^\+[1-9]\d{2,19}$/,
        'Must be in E.164 format (e.g. +61412345678).'
      )(v);
      if (pattern !== true) return pattern;
      if (!isValidPhoneNumber(v)) return 'Phone number is not a valid phone number';
      return true;
    },

    validateAuthenticationType(v) {
      if (!v) return true;
      return validationRules.oneOf('Authentication type', ['Internal', 'External'])(v);
    },

    validateRole(v) {
      if (!v) return true;
      return validationRules.oneOf('Role', ['TenantAdministrator', 'Support', 'User'])(v);
    },

    validateProductInstances(v) {
      if (!v) return true;
      return validationRules.pattern(
        'Product instances',
        /^(default|[^:|]+:[^:|]+(\|[^:|]+:[^:|]+)*)$/,
        'Must be "default" or <productCode>:<instanceId> separated by |.'
      )(v);
    },

    validateForm() {
      const errors = {
        tenantSubDomain: this.validateTenantSubDomain(this.formFields.tenantSubDomain),
        username: this.validateUsername(this.formFields.username),
        emailAddress: this.validateEmailAddress(this.formFields.emailAddress),
        familyName: this.validateFamilyName(this.formFields.familyName),
        givenName: this.validateGivenName(this.formFields.givenName),
        displayName: this.validateDisplayName(this.formFields.displayName),
        preferredLocale: this.validatePreferredLocale(this.formFields.preferredLocale),
        status: this.validateStatus(this.formFields.status),
        title: this.validateTitle(this.formFields.title),
        phoneNumber: this.validatePhoneNumber(this.formFields.phoneNumber),
        authenticationType: this.validateAuthenticationType(this.formFields.authenticationType),
        role: this.validateRole(this.formFields.role),
        productInstances: this.validateProductInstances(this.formFields.productInstances)
      };

      this.formErrors = Object.fromEntries(
        Object.entries(errors).map(([key, val]) => [key, val === true ? '' : val])
      );

      return Object.values(errors).every((v) => v === true);
    },

    async onSaveClick() {
      if (this.inputFields.length === 0) {
        this.showValidationErrorDialog = true;
        this.validationErrorMessage = 'At least one User record(s) is required to save.';
        return;
      }

      try {
        if (this.saveInProgress) return;

        this.saveInProgress = true;

        const userInfo = this.inputFields.map((item) =>
          mapUserFields(item, this.customAttributeColumns, true)
        );

        await this.$store.dispatch('hub-migration/postCsvUsers', {
          clientSid: this.clientSid,
          userInfo
        });
        this.showSaveSuccessDialog = true;
        this.$emit('save-success');
        await this.fetchHubCsvUsers();
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || error.message;
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.showSaveErrorDialog = true;
      } finally {
        this.saveInProgress = false;
      }
    },

    async fetchHubCsvUsers() {
      if (this.fetchHubCsvUsersInProgress) return;
      this.fetchHubCsvUsersInProgress = true;

      try {
        const users = await this.$store.dispatch('hub-migration/getCsvUsers', {
          clientSid: this.clientSid
        });
        const allKeys = Object.keys(users.userInfo[0] || {});
        const customCols = allKeys.filter((k) => !KNOWN_API_KEYS.has(k));
        this.customAttributeColumns = customCols;

        this.inputFields = users.userInfo.map((user) => mapUserFields(user, customCols));
      } catch (err) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.fetchHubCsvUsersErrorMessage = err?.response?.data?.message || err.message;
        this.fetchHubCsvUsersErrorDialog = true;
      } finally {
        this.fetchHubCsvUsersInProgress = false;
      }
    }
  }
};
</script>
